import { intl } from "LocaleWrapper";
import { useQuery } from "@apollo/client";
import { Alert, CircularProgress } from "@mui/material";
import {
	CargoPlanningChecksDocument,
	PlanningCheckFieldsFragment,
	VoyagePlanningChecksDocument,
} from "__generated__/components/planningChecks/graphql/planningChecks.queries.generated";
import { PlanningCheckSeverity } from "__generated__/enums";
import { Button } from "@cofano/cck";
import { useConfirm } from "components/hooks/useConfirm.tsx";
import { FormattedDate } from "utils/DateUtils.tsx";

type PlanningChecksBaseProps = {
	onlyShowWarnings?: boolean,
};

export type PlanningChecksProps = PlanningChecksBaseProps & ({
	shipId: number | null | undefined,
	cargoIds: number[] | null | undefined,
	voyageId?: never,
} | {
	voyageId: number | null | undefined,
	shipId?: never,
	cargoIds?: never,
});

const describeCheck = (check: PlanningCheckFieldsFragment): string => {
	if (!check.languageKey) {
		return check.message ?? "";
	}

	return intl.formatMessage({ id: check.languageKey }, (check.languageParams ?? {}) as Record<string, string>,
	);
};

const PlanningChecks = ({ shipId, cargoIds, voyageId }: PlanningChecksProps) => {
	const hasVoyage = !!voyageId;
	const hasCargos = !!shipId && !!cargoIds;

	const confirm = useConfirm();

	const cargoChecks = useQuery(CargoPlanningChecksDocument, {
		variables: { shipId: shipId!, cargoIds: cargoIds! },
		skip: hasVoyage || !hasCargos,
		context: { silentError: () => true },
	});

	const voyagePlanChecks = useQuery(VoyagePlanningChecksDocument, {
		variables: { voyageId: voyageId! },
		skip: !hasVoyage,
		context: { silentError: () => true },
	});

	const { loading, error } = hasVoyage ? voyagePlanChecks : cargoChecks;
	const warnings = hasVoyage
		? voyagePlanChecks.data?.voyagePlanningChecks.warnings
		: cargoChecks.data?.cargoPlanningChecks.warnings;

	const hasErrorSeverity = warnings?.find(w => w.severity == PlanningCheckSeverity.Error);

	const showWarningsDialog = () => {
		confirm({
			type: "WARNING",
			title: intl.formatMessage({ id: "WARNING" }),
			content: <div>
				{warnings?.map((check, index) => (
					<Alert
						key={index}
						severity={check.severity == PlanningCheckSeverity.Error ? "error" : "warning"}
						sx={{ mb: 1 }}
						action={<div style={{ marginTop: 6, marginRight: 8 }}>
							<FormattedDate date={check.timestamp} />
						</div>
						}
					>
						<div style={{ marginTop: 2 }}>
							{describeCheck(check)}
						</div>
					</Alert>
				))}
			</div>,
			confirmText: intl.formatMessage({ id: "OK" }),
			hideCancel: true,
		}).catch(() => undefined);
	};

	if (!hasVoyage && !hasCargos) {
		return <></>;
	}

	if (error) {
		return <Alert
			severity="error"
			sx={{ mb: 1 }}
		>
			<div style={{ marginTop: 2 }}>
				{error.message}
			</div>
		</Alert>;
	}

	// loading
	if (loading) {
		return <Alert
			severity="info"
			action={<CircularProgress size={24} />}
			sx={{ mb: 1 }}
		>
			<div style={{ marginTop: 2 }}>
				{intl.formatMessage({ id: "VOYAGE_CHECK_CHECKING" })}
			</div>
		</Alert>;
	}

	// no warnings
	if (warnings?.length == 0) {
		return <Alert
			severity="success"
			sx={{ mb: 1 }}
		>
			<div style={{ marginTop: 2 }}>
				{intl.formatMessage({ id: "VOYAGE_CHECK_SHIP_COMPATIBLE" })}
			</div>
		</Alert>;
	}

	// single warning, show it directly
	if (warnings?.length == 1) {
		const check = warnings[0];
		return <Alert
			severity={check.severity == PlanningCheckSeverity.Error ? "error" : "warning"}
			style={{ marginBottom: 16 }}
			action={<div style={{ marginTop: 6, marginRight: 8 }}>
				<FormattedDate date={check.timestamp} />
			</div>}
		>
			<div style={{ marginTop: 2 }}>
				{describeCheck(check)}
			</div>
		</Alert>;
	}

	return <>
		{warnings && <Alert
			severity={hasErrorSeverity ? "error" : "warning"}
			style={{ marginBottom: 16 }}
			action={
				<Button
					variant="outlined"
					size="small"
					onClick={showWarningsDialog}>
					{intl.formatMessage({ id: "CHECK" })}
				</Button>}>
			{intl.formatMessage({ id: hasErrorSeverity ? "SHIP_PLANNING_HAS_ERRORS" : "SHIP_PLANNING_HAS_WARNINGS" }).replace("_X_", warnings.length + "")}
		</Alert>}
	</>;
};

export default PlanningChecks;
